/* eslint-disable no-plusplus */
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@glu/theming';
import { useClickOutside, useTrapFocus } from '@glu/utilities-react';
import classnames from 'classnames';

import {
  Button, PRIMARY, TERTIARY, SECONDARY
} from '@glu/buttons-react';
import styles from './Modal.styles';
import ResizeHandle from './ResizeHandle';
import MoveHandle from './MoveHandle';
import ModalHeader from './ModalHeader';
import useBodyScrollLock from './useBodyScrollLock';

const useStyles = createUseStyles(styles);
let uniqueId = 0;

const Modal = (props) => {
  const {
    ariaDescribedby,
    ariaLabel,
    ariaLabelledby,
    btnCloseEnabled,
    btnPrimaryAttributes,
    btnPrimaryText,
    btnSecondaryAttributes,
    btnSecondaryText,
    btnTertiaryAttributes,
    btnTertiaryText,
    children,
    classes: modalClasses,
    closeOnClickAway,
    contentHasOverflow,
    dataQa,
    header,
    height,
    htmlId,
    maxContentHeight,
    minHeight,
    minWidth,
    movable,
    onBtnPrimaryClick,
    onBtnSecondaryClick,
    onBtnTertiaryClick,
    onClose,
    onOpen,
    open,
    overlayScreen,
    posX,
    posY,
    resizable,
    secondaryHeader,
    takeover,
    type,
    useHeaderElem,
    width
  } = props;

  const classes = useStyles(useMemo(() => ({
    posX, posY, width, minWidth, minHeight, maxContentHeight, classes: modalClasses
  }), [posY, posX, width, minWidth, minHeight, maxContentHeight, modalClasses]));

  const id = useMemo(() => htmlId || `input-${uniqueId++}`, [htmlId]);

  const [resizedWidth, setResizedWidth] = useState(width);
  const [resizedHeight, setResizedHeight] = useState(height);
  const [movedX, setMovedX] = useState(posX);
  const [movedY, setMovedY] = useState(posY);
  useEffect(() => {
    if (onOpen) { onOpen(); }
  }, [onOpen, open]);

  useEffect(() => {
    setResizedWidth(width);
  }, [width]);

  useEffect(() => {
    setResizedHeight(height);
  }, [height]);

  /* istanbul ignore next */
  const handleClickOutside = () => {
    if (closeOnClickAway && onClose) {
      onClose();
    }
  };

  const ref = useRef();
  useClickOutside(ref, handleClickOutside);
  useBodyScrollLock(open);
  const onKeyDown = useTrapFocus({ ref, active: open });

  return open ? (
    <div
      data-qa="modal-overlay"
      className={
        classnames({
          [classes.overlay]: !resizable && !movable && !takeover,
          [classes.overlayScreen]: overlayScreen,
          [classes.transparentOverlay]: movable
        })
      }
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        style={takeover ? {} : {
          width: resizedWidth,
          height: resizedHeight,
          top: `calc(${posY || posY === 0 ? `${posY}px` : '50%'} + ${movedY}px)`,
          left: `calc(${posX || posX === 0 ? `${posX}px` : '50%'} + ${movedX}px)`
        }}
        className={classnames(
          takeover ? classes.takeover : classes.card,
          classes[type],
          {
            [classes.dynamic]: resizable
              || (movable && ((posX || posX === 0) || (posY || posY === 0))),
            [classes.hasType]: !!type
          }
        )}
        data-qa={dataQa || id}
        ref={ref}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
      >
        <ModalHeader
          type={type}
          btnCloseEnabled={btnCloseEnabled}
          onClose={onClose}
          useHeaderElem={useHeaderElem}
          header={header}
          secondaryHeader={secondaryHeader}
          ariaLabelledby={ariaLabelledby}
          ariaDescribedby={ariaDescribedby}
        />
        <div className={classnames(classes.content, {
          [classes.contentHasOverflow]: contentHasOverflow,
          [classes.hasNoHeader]: !header
        })}
        >
          {children}
        </div>
        {(btnPrimaryText || btnSecondaryText || btnTertiaryText) && (
          <div className={classes.actions}>
            {btnPrimaryText && (
            <Button data-qa="primaryButton" onClick={onBtnPrimaryClick} variant={PRIMARY} {...btnPrimaryAttributes}>
              {btnPrimaryText}
            </Button>
            )}
            {btnSecondaryText && (
            <Button data-qa="secondaryButton" onClick={onBtnSecondaryClick} variant={SECONDARY} {...btnSecondaryAttributes}>
              {btnSecondaryText}
            </Button>
            )}
            {btnTertiaryText && (
            <Button data-qa="tertiaryButton" onClick={onBtnTertiaryClick} variant={TERTIARY} {...btnTertiaryAttributes}>
              {btnTertiaryText}
            </Button>
            )}
          </div>
        )}
        {movable && (
          <MoveHandle
            setMovedX={setMovedX}
            setMovedY={setMovedY}
            {...props}
          />
        )}
        {resizable && (
          <ResizeHandle
            cardElem={ref}
            setResizedWidth={setResizedWidth}
            setResizedHeight={setResizedHeight}
            {...props}
          />
        )}
      </div>
    </div>
  ) : null;
};

Modal.propTypes = {
  /** Toggle if 'X' button in top right is displayed */
  btnCloseEnabled: PropTypes.bool,
  /** Content of primary button. Button will not display if undefined */
  btnPrimaryText: PropTypes.string,
  /** Content of secondary button. Button will not display if undefined */
  btnSecondaryText: PropTypes.string,
  /** Content of tertiary button. Button will not display if undefined */
  btnTertiaryText: PropTypes.string,
  /** Content of modal */
  children: PropTypes.node,
  /** Toggle if the modal will close when the user clicks outside of it */
  closeOnClickAway: PropTypes.bool,
  /** Attribute used for identification in testing */
  dataQa: PropTypes.string,
  /** Headline text */
  header: PropTypes.string,
  /** Explict height of the modal card. */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** explicity set html id attribute */
  htmlId: PropTypes.string,
  /** Limits height of content. Longer content will scroll.  */
  maxContentHeight: PropTypes.number,
  /** Minimum height of the modal. Limits resize function */
  minHeight: PropTypes.number,
  /** Minimum width of the modal. Limits resize function */
  minWidth: PropTypes.number,
  /** Object of custom classes of modal */
  classes: PropTypes.shape({
    /** Custom class for overlay block */
    overlay: PropTypes.string,
    transparentOverlay: PropTypes.string,
    /** Custom class for overlayScreen block */
    overlayScreen: PropTypes.string,
    /** Custom class for takeover block */
    takeover: PropTypes.string,
    /** Custom class for card block */
    card: PropTypes.string,
    /** Custom class for dynamic block */
    dynamic: PropTypes.string,
    /** Custom class for hasType block */
    hasType: PropTypes.string,
    /** Custom class for content block */
    content: PropTypes.string,
    /** Custom class for contentHasOverflow block */
    contentHasOverflow: PropTypes.string,
    /** Custom class for hasNoHeader block */
    hasNoHeader: PropTypes.string,
    /** Custom class for actions block */
    actions: PropTypes.string
  }),
  /** Toggle if the modal can be moved by dragging on the padding above the header */
  movable: PropTypes.bool,
  /** Callback for primary button */
  onBtnPrimaryClick: PropTypes.func,
  /** Callback for secondary button */
  onBtnSecondaryClick: PropTypes.func,
  /** Callback for tertiary button */
  onBtnTertiaryClick: PropTypes.func,
  /** Callback for close 'X' button */
  onClose: PropTypes.func,
  /** Callback when modal is moved. Only enabled if movable is true */
  onMove: PropTypes.func,
  /** Callback when modal is open. Equivalent to "onRender" */
  onOpen: PropTypes.func,
  /** Callback when modal is resized. Triggers after resizing is complete */
  onResize: PropTypes.func,
  /** Toggle display of modal */
  open: PropTypes.bool.isRequired,
  /** Toggle background shade which covers page content */
  overlayScreen: PropTypes.bool,
  /** Explicitly defined X coordinate position. Useful with "movable" property */
  posX: PropTypes.number,
  /** Explicitly defined Y coordinate position. Useful with "movable" property */
  posY: PropTypes.number,
  /** Toggle if resize handle is displayed and if user can adjust size of modal card */
  resizable: PropTypes.bool,
  /** Text for secondary hard. Appears inline with regular header if present */
  secondaryHeader: PropTypes.string,
  /** Toggle if modal fills its container. Use with caution.
   * It may be better to use a route instead of a modal */
  takeover: PropTypes.bool,
  /** Message type. Warning, Info, Error, Success */
  type: PropTypes.string,
  /** Explict width of the modal card. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Additional primary button attributes (i.e. aria-busy) */
  btnPrimaryAttributes: PropTypes.shape({}),
  /** Additional secondary button attributes (i.e. aria-busy) */
  btnSecondaryAttributes: PropTypes.shape({}),
  /** Additional tertiary button attributes (i.e. aria-busy) */
  btnTertiaryAttributes: PropTypes.shape({}),
  /**
   * If your content is long, this boolean
   * will allow you to have overflowY set (true by default)
   */
  contentHasOverflow: PropTypes.bool,
  /** For cases where there is no visible modal title present */
  ariaLabel: PropTypes.string,
  /**
   * For cases where this is a visible modal title present.
   * Set this value to the id of the title element
   */
  ariaLabelledby: PropTypes.string,
  /**
   * For cases where the dialog contains additional descriptive text
   * besides the dialog title use this attribute to define it.
   * Set this value to the id of the description element.
   */
  ariaDescribedby: PropTypes.string,
  /** Default element wrapping modal title is div.
   * Set this to true to use <h2> instead)
   */
  useHeaderElem: PropTypes.bool
};

Modal.defaultProps = {
  btnCloseEnabled: true,
  btnPrimaryText: undefined,
  btnSecondaryText: undefined,
  btnTertiaryText: undefined,
  children: undefined,
  classes: undefined,
  closeOnClickAway: true,
  dataQa: undefined,
  header: undefined,
  height: undefined,
  htmlId: undefined,
  maxContentHeight: undefined,
  minHeight: undefined,
  minWidth: undefined,
  movable: false,
  onBtnPrimaryClick: undefined,
  onBtnSecondaryClick: undefined,
  onBtnTertiaryClick: undefined,
  onClose: undefined,
  onMove: undefined,
  onOpen: undefined,
  onResize: undefined,
  overlayScreen: true,
  posX: undefined,
  posY: undefined,
  resizable: false,
  secondaryHeader: undefined,
  takeover: false,
  type: undefined,
  width: undefined,
  btnPrimaryAttributes: undefined,
  btnSecondaryAttributes: undefined,
  btnTertiaryAttributes: undefined,
  contentHasOverflow: true,
  ariaLabel: undefined,
  ariaLabelledby: undefined,
  ariaDescribedby: undefined,
  useHeaderElem: false
};

Modal.displayName = 'Modal';
export default Modal;
